import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { TrendingUp, Sparkles } from 'lucide-react';

interface Prop9PanelProps {
  show: boolean;
  base: number;
  data: {
    sumOfAllEntries: number;
    rootTerm: number;
    lhs: number;
    rhs: number;
    nextPowerVal: number;
  } | null;
}

const Prop9Panel: React.FC<Prop9PanelProps> = ({ show, data, base }) => {
  return (
    <AnimatePresence>
      {show && data && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          className="relative w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-blue-500/30 bg-zinc-900/90 p-4 shadow-2xl backdrop-blur-xl sm:w-80 sm:p-5"
        >
          <div className="absolute top-0 right-0 p-3 opacity-10">
            <TrendingUp className="h-12 w-12 text-blue-500" />
          </div>

          <div className="mb-4 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-400" />
            <h3 className="text-xs font-bold tracking-widest text-blue-400 uppercase">
              Proposition 9 Verification
            </h3>
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <div className="flex justify-between font-mono text-[10px] text-zinc-500 uppercase">
                Σ All Entries + a/(b-1)
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-white">
                  {data.sumOfAllEntries}
                </span>
                <span className="text-zinc-600">+</span>
                <span className="font-mono text-sm text-white">
                  {data.rootTerm}
                </span>
                <span className="font-bold text-blue-500">=</span>
                <span className="font-mono text-sm font-bold text-blue-400">
                  {data.lhs}
                </span>
              </div>
            </div>

            <div className="h-px bg-zinc-800" />

            <div className="space-y-1">
              <div className="flex justify-between font-mono text-[10px] text-zinc-500 uppercase">
                Target: a·b<sup>c+1</sup> / (b-1)
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-white">
                  {data.nextPowerVal}
                </span>
                <span className="text-zinc-600">/</span>
                <span className="font-mono text-sm text-white">{base - 1}</span>
                <span className="font-bold text-blue-500">=</span>
                <span className="font-mono text-sm font-bold text-blue-400">
                  {data.rhs}
                </span>
              </div>
            </div>

            {Math.abs(data.lhs - data.rhs) < 0.01 ? (
              <div className="flex items-center gap-2 rounded border border-emerald-500/20 bg-emerald-500/10 px-2 py-1">
                <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                <span className="text-[10px] font-bold text-emerald-500 uppercase">
                  Identity Verified
                </span>
              </div>
            ) : (
              <div className="text-[10px] text-zinc-500 italic">
                Calculating convergence...
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Prop9Panel;
